import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/customer-ui/button'
import { Input } from '@/components/customer-ui/input'
import { Label } from '@/components/ui/label'
import { FileText, Info, Save, Upload, X } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/customer-ui/tooltip'
import { Textarea } from '@/components/customer-ui/textarea'
import { toast } from '@/components/customer-ui/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Customer } from '@modules/manifera-user/resources/js/schema'
import client from '@/http/customer-client'
import { validateIBAN } from '@/utils/helpers'
import { AxiosError, isAxiosError } from 'axios'
import { ApiError } from '@/lib/restApiErrorHandler'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '@modules/manifera-user/resources/js/components/confirm-dialog'

interface DraftDocument {
  id: string
  name: string
  path?: string
  size?: number
  mime_type?: string
  url?: string
}

const MAX_FILES = 5
const MAX_FILE_SIZE_MB = 5
const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp'

export default function GeneralInfoTab({
  customer,
  onSaveSuccess,
  fetchAccount
}: {
  customer?: Customer
  onSaveSuccess?: () => void
  fetchAccount?: () => void
}) {
  const [documentSelected, setDocumentSelected] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const [storedDocuments, setStoredDocuments] = useState<DraftDocument[]>([])
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [maxCountComment, setMaxCountComment] = useState(0)
  const [requiredFieldErrors, setRequiredFieldErrors] = useState({
    firstName: false,
    lastName: false,
    houseNumber: false,
    postalCode: false
  })
  const [errors, setErrors] = useState({
    isError: false,
    message: ''
  })
  const [success, setSuccess] = useState({
    isSuccess: false,
    message: ''
  })
  const emptyFormData = {
    firstName: '',
    lastName: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    phone: '',
    ean18: '',
    accountHolder: '',
    iban: '',
    notes: '',
    kortingscode: ''
  }

  const [formData, setFormData] = useState(() => ({
    ...emptyFormData
  }))

  useEffect(() => {
    if (!customer) {
      return
    }

    const docs = Array.isArray(customer.documents)
      ? customer.documents
          .map((item): DraftDocument => {
            const id = typeof item.id === 'string' ? item.id : ''
            return {
              id,
              name: typeof item.name === 'string' ? item.name : 'document',
              path: typeof item.path === 'string' ? item.path : undefined,
              size: typeof item.size === 'number' ? item.size : undefined,
              mime_type:
                typeof item.mime_type === 'string' ? item.mime_type : undefined,
              url: id ? `/app-api/me/documents/${id}/download` : undefined
            }
          })
          .filter((doc) => doc.id !== '')
      : []
    setStoredDocuments(docs)

    const customerInfo: Record<keyof typeof emptyFormData, string> = {
      firstName: customer.first_name ?? '',
      lastName: customer.last_name ?? '',
      street: customer.street ?? '',
      houseNumber: customer.house_number ?? '',
      postalCode: customer.postcode ?? '',
      city: customer.place ?? '',
      phone: customer.phone_number ?? '',
      ean18: customer.ean_number ?? '',
      accountHolder: customer.account_holder_name ?? '',
      iban: customer.iban_number ?? '',
      notes: customer.comments ?? '',
      kortingscode: customer.partner_discount_code ?? ''
    }

    setFormData((prev) => {
      const next = { ...prev }
      let changed = false

      for (const [key, value] of Object.entries(customerInfo) as Array<
        [keyof typeof emptyFormData, string]
      >) {
        if (next[key].trim() === '' && value.trim() !== '') {
          next[key] = value
          changed = true
        }
      }

      if (changed) {
        return next
      }

      return prev
    })
  }, [customer])

  const ibanError = useMemo(() => {
    if (!formData.iban) return ''
    return validateIBAN(formData.iban) ? '' : t('Invalid IBAN number')
  }, [formData.iban, t])

  const eanError = useMemo(() => {
    if (!formData.ean18) return ''

    return /^871\d{15}$/.test(formData.ean18) ? '' : t('Invalid EAN-18 number')
  }, [formData.ean18, t])

  const hasRequiredFieldErrors = useMemo(() => {
    return Object.values(requiredFieldErrors).some(Boolean)
  }, [requiredFieldErrors])

  const hasFormErrors =
    hasRequiredFieldErrors ||
    Boolean(ibanError) ||
    Boolean(eanError) ||
    maxCountComment === 1500

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    if (!selected) return

    const filesToUpload: File[] = []
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i]
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setErrors({
          isError: true,
          message: `${file.name} ${t('File is larger than')} ${MAX_FILE_SIZE_MB}MB`
        })
        continue
      }
      if (storedDocuments.length + filesToUpload.length >= MAX_FILES) {
        setErrors({
          isError: true,
          message: `${t('Maximum files allowed')} ${MAX_FILES}`
        })
        break
      }
      filesToUpload.push(file)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''

    if (filesToUpload.length === 0) {
      return
    }

    void (async () => {
      setIsUploadingDocuments(true)
      try {
        const form = new FormData()
        filesToUpload.forEach((file) => {
          form.append('documents[]', file)
        })

        const response = await client.post<{ data: DraftDocument[] }>(
          '/app-api/me/documents',
          form,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        const nextDocuments = Array.isArray(response.data.data)
          ? response.data.data.map((doc) => ({
              ...doc,
              url: `/app-api/me/documents/${doc.id}/download`
            }))
          : storedDocuments
        setStoredDocuments(nextDocuments)
        queryClient.setQueryData(
          ['customer-account'],
          (prev: Customer | undefined) =>
            prev
              ? {
                  ...prev,
                  documents: nextDocuments
                }
              : prev
        )
        setSuccess({
          isSuccess: true,
          message: t('Document uploaded')
        })
      } catch {
        setErrors({
          isError: true,
          message: t('Document upload failed')
        })
      } finally {
        setIsUploadingDocuments(false)
        setIsFormDirty(true)
      }
    })()
  }

  const removeStoredDocument = async (id: string) => {
    try {
      const response = await client.delete<{ data: DraftDocument[] }>(
        `/app-api/me/documents/${id}`
      )
      const nextDocuments = Array.isArray(response.data.data)
        ? response.data.data
        : storedDocuments.filter((doc) => doc.id !== id)
      setStoredDocuments(nextDocuments)
      queryClient.setQueryData(
        ['customer-account'],
        (prev: Customer | undefined) =>
          prev
            ? {
                ...prev,
                documents: nextDocuments
              }
            : prev
      )

      setSuccess({
        isSuccess: true,
        message: t('Document deleted')
      })
    } catch {
      setErrors({
        isError: true,
        message: t('Document delete failed')
      })
    }
  }

  const setField = (field: string, value: string) => {
    setIsFormDirty(true)
    if (
      field === 'firstName' ||
      field === 'lastName' ||
      field === 'houseNumber' ||
      field === 'postalCode'
    ) {
      setRequiredFieldErrors((prev) => ({
        ...prev,
        [field]: value.trim() === ''
      }))
    }
    setFormData((prev) => {
      return { ...prev, [field]: value }
    })
  }

  const handleDialogClose = () => {
    setShowSuccessDialog(false)
    onSaveSuccess?.()
  }

  const handleDelete = () => {
    if (documentSelected) {
      void removeStoredDocument(documentSelected)
    }
  }

  const charCount = formData.notes.length

  return (
    <>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()

          const missingFirstName = formData.firstName.trim() === ''
          const missingLastName = formData.lastName.trim() === ''
          const missingHouseNumber = formData.houseNumber.trim() === ''
          const missingPostalCode = formData.postalCode.trim() === ''

          if (
            missingFirstName ||
            missingLastName ||
            missingHouseNumber ||
            missingPostalCode
          ) {
            setRequiredFieldErrors({
              firstName: missingFirstName,
              lastName: missingLastName,
              houseNumber: missingHouseNumber,
              postalCode: missingPostalCode
            })
            return
          }

          if (ibanError) {
            toast({
              description: t('Check the IBAN number'),
              variant: 'destructive'
            })
            return
          }

          if (eanError) {
            toast({
              description: t('Check the EAN-18 number'),
              variant: 'destructive'
            })
            return
          }

          if (formData.firstName.length > 255) {
            setErrors({
              isError: true,
              message: t('Voornaam mag maximaal 255 tekens bevatten')
            })
            return
          }

          if (formData.lastName.length > 255) {
            setErrors({
              isError: true,
              message: t('Achternaam mag maximaal 255 tekens bevatten')
            })
            return
          }

          void (async () => {
            setIsSaving(true)
            try {
              await client.put('/app-api/me', {
                first_name: formData.firstName,
                last_name: formData.lastName,
                street: formData.street,
                house_number: formData.houseNumber,
                postcode: formData.postalCode,
                place: formData.city,
                phone_number: formData.phone,
                ean_number: formData.ean18 || null,
                account_holder_name: formData.accountHolder,
                iban_number: formData.iban,
                comments: formData.notes || null,
                partner_discount_code: formData.kortingscode || null
              })

              setShowSuccessDialog(true)
            } catch (e) {
              if (isAxiosError(e)) {
                const error = e as AxiosError<ApiError>
                if (error.status === 422) {
                  setErrors({
                    isError: true,
                    message: error.response?.data.message ?? ''
                  })
                  return
                }
              }
              setErrors({
                isError: true,
                message: t('Save failed, please try again.')
              })
            } finally {
              setIsSaving(false)
              fetchAccount?.()
              setIsFormDirty(false)
            }
          })()
        }}
        className='bg-card border-input rounded-lg border p-6'
      >
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='firstName'>
              {t('First name')} <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='firstName'
              value={formData.firstName}
              onChange={(e) => {
                setField('firstName', e.target.value)
              }}
              placeholder='Jan'
              className={
                requiredFieldErrors.firstName ? 'border-destructive' : ''
              }
              style={
                requiredFieldErrors.firstName
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.firstName}
            />
            {requiredFieldErrors.firstName && (
              <p className='text-destructive text-sm'>
                {t('page_change_password.error.required_field', {
                  field: t('First name').toLowerCase()
                })}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='lastName'>
              {t('Last name')} <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='lastName'
              value={formData.lastName}
              onChange={(e) => {
                setField('lastName', e.target.value)
              }}
              placeholder='De Vries'
              className={
                requiredFieldErrors.lastName ? 'border-destructive' : ''
              }
              style={
                requiredFieldErrors.lastName
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.lastName}
            />
            {requiredFieldErrors.lastName && (
              <p className='text-destructive text-sm'>
                {t('page_change_password.error.required_field', {
                  field: t('Last name').toLowerCase()
                })}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='street'>{t('Street')}</Label>
            <Input
              id='street'
              value={formData.street}
              onChange={(e) => {
                setField('street', e.target.value)
              }}
              placeholder='Keizersgracht'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='houseNumber'>
              {t('House number')} <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='houseNumber'
              value={formData.houseNumber}
              onChange={(e) => {
                setField('houseNumber', e.target.value)
              }}
              placeholder='123'
              className={
                requiredFieldErrors.houseNumber ? 'border-destructive' : ''
              }
              style={
                requiredFieldErrors.houseNumber
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.houseNumber}
            />
            {requiredFieldErrors.houseNumber && (
              <p className='text-destructive text-sm'>
                {t('page_change_password.error.required_field', {
                  field: t('House number').toLowerCase()
                })}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='postalCode'>
              {t('Postal code')} <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='postalCode'
              value={formData.postalCode}
              onChange={(e) => {
                setField('postalCode', e.target.value)
              }}
              placeholder='1015 AB'
              className={
                requiredFieldErrors.postalCode ? 'border-destructive' : ''
              }
              style={
                requiredFieldErrors.postalCode
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.postalCode}
            />
            {requiredFieldErrors.postalCode && (
              <p className='text-destructive text-sm'>
                {t('page_change_password.error.required_field', {
                  field: t('Postal code').toLowerCase()
                })}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='city'>{t('City')}</Label>
            <Input
              id='city'
              value={formData.city}
              onChange={(e) => {
                setField('city', e.target.value)
              }}
              placeholder='Amsterdam'
            />
          </div>
          <div className='space-y-2'>
            <div className='flex h-4.25 items-center'>
              <Label htmlFor='phone' className='leading-none'>
                {t('Phone number')}
              </Label>
            </div>
            <Input
              id='phone'
              type='tel'
              value={formData.phone}
              onChange={(e) => {
                setField('phone', e.target.value)
              }}
              placeholder='+31 6 12345678'
            />
          </div>
          <div className='space-y-2'>
            <div className='flex h-4.25 items-center gap-1.5'>
              <Label htmlFor='ean18' className='leading-none'>
                {t('EAN-18 number')}
              </Label>
              <TooltipProvider delayDuration={0}>
                <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                  <TooltipTrigger asChild>
                    <Info
                      className='text-muted-foreground h-4 w-4 shrink-0 cursor-help'
                      onClick={() => {
                        setTooltipOpen((prev) => !prev)
                      }}
                    />
                  </TooltipTrigger>

                  <TooltipContent className='max-w-xs'>
                    <p>
                      {t(
                        'The EAN-18 number (also called EAN code) is a unique identification number for your electricity connection. You can find it on your energy bill or contract. It starts with 871 and consists of 18 digits.'
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id='ean18'
              value={formData.ean18}
              onChange={(e) => {
                setField('ean18', e.target.value)
              }}
              placeholder='871000000000000000'
              className={eanError ? 'border-destructive' : ''}
              style={
                eanError ? { borderColor: 'var(--destructive)' } : undefined
              }
              aria-invalid={Boolean(eanError)}
            />
            {eanError && <p className='text-destructive text-sm'>{eanError}</p>}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='accountHolder'>{t('Account holder name')}</Label>
            <Input
              id='accountHolder'
              value={formData.accountHolder}
              onChange={(e) => {
                setField('accountHolder', e.target.value)
              }}
              placeholder='J. de Vries'
            />
          </div>
          <div className='space-y-2'>
            <div className='flex h-4.25 items-center gap-1.5'>
              <Label htmlFor='iban'>{t('IBAN number')}</Label>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className='text-muted-foreground h-4 w-4 shrink-0 cursor-help' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      {t(
                        'The account number consists of 18 letters and numbers and begins with the country code. In the Netherlands, this is NL.'
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id='iban'
              value={formData.iban}
              onChange={(e) => {
                setField('iban', e.target.value)
              }}
              placeholder='NL00 BANK 0000 0000 00'
              className={ibanError ? 'border-destructive' : ''}
              style={
                ibanError ? { borderColor: 'var(--destructive)' } : undefined
              }
              aria-invalid={Boolean(ibanError)}
            />
            {ibanError && (
              <p className='text-destructive text-sm'>{ibanError}</p>
            )}
          </div>
          {/* <div className='space-y-2'>
            <Label htmlFor='commissieZeres'>{t('Zeres commission')}</Label>
            <div className='flex items-center gap-1'>
              <Input
                id='commissieZeres'
                value='19'
                readOnly
                className='bg-muted pointer-events-none cursor-not-allowed'
                endIcon={
                  <span className='text-muted-foreground shrink-0 text-sm'>
                    %
                  </span>
                }
              />
            </div>
          </div> */}
          <div className='space-y-2'>
            <div className='flex h-4.25 items-center gap-1.5'>
              <Label htmlFor='kortingscode'>
                {t('Discount code')} ({t('optional')})
              </Label>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className='text-muted-foreground h-4 w-4 shrink-0 cursor-help' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      {t(
                        'If you have received a discount code from one of our partners, please enter it here.'
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id='kortingscode'
              value={formData.kortingscode || ''}
              onChange={(e) => {
                setField('kortingscode', e.target.value)
              }}
              placeholder='CODE1234'
            />
          </div>
          <div className='space-y-2 sm:col-span-2'>
            <Label className='inline-block' htmlFor='notes'>
              {t('Notes', {
                max: 1500 - (1500 - charCount)
              })}
            </Label>
            <Textarea
              id='notes'
              value={formData.notes}
              onChange={(e) => {
                setField('notes', e.target.value)
                if (e.target.value.length > 1500) {
                  setMaxCountComment(1500)
                }
              }}
              placeholder='Eventuele opmerkingen...'
              rows={5}
              aria-rowcount={5}
              className='resize-none'
            />
            {maxCountComment == 1500 && (
              <p className='text-destructive text-sm'>
                {t('Max 1500 characters')}
              </p>
            )}
          </div>
          <div className='space-y-2 sm:col-span-2'>
            <Label className='inline-block'>
              {t('Documents')} (ieder maximaal {MAX_FILE_SIZE_MB}
              MB)
            </Label>
            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPTED_TYPES}
              multiple
              onChange={handleFileChange}
              className='hidden'
            />
            {storedDocuments.length < MAX_FILES && (
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='border-border hover:border-primary/50 hover:bg-accent/30 group w-full cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors'
              >
                <Upload className='text-muted-foreground group-hover:text-primary mx-auto mb-1 h-6 w-6 transition-colors' />
                <span className='text-muted-foreground group-hover:text-foreground text-sm transition-colors'>
                  {t('Click to select files')}
                </span>
              </button>
            )}
            {storedDocuments.length > 0 && (
              <div className='mt-2 space-y-2'>
                {storedDocuments.map((f) => (
                  <div
                    key={f.id}
                    className='bg-muted/50 flex items-center gap-3 rounded-md p-3'
                  >
                    <FileText className='text-primary h-5 w-5 shrink-0' />
                    <a
                      className='text-foreground hover:text-primary flex-1 truncate text-sm hover:underline'
                      download={f.path}
                      href={f.url}
                    >
                      {f.name}
                    </a>
                    <button
                      type='button'
                      onClick={() => {
                        setDocumentSelected(f.id)
                        setShowDeleteDialog(true)
                      }}
                      className='text-muted-foreground hover:text-destructive shrink-0 cursor-pointer transition-colors'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {isUploadingDocuments && (
              <p className='text-muted-foreground text-sm'>
                {t('Uploading...')}
              </p>
            )}
          </div>
          <div className='flex justify-center pt-2 sm:col-span-2'>
            <Button
              type='submit'
              size='lg'
              className='gap-2'
              disabled={isSaving || !isFormDirty || hasFormErrors}
            >
              <Save className='h-4 w-4' />
              <span>{isSaving ? t('Saving...') : t('Save')}</span>
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
        }}
        onConfirm={handleDelete}
        title='Delete Document'
        description='Are you sure you want to delete this document? This action cannot be undone.'
        btnCancel='button.cancel'
        btnConfirm='button.delete'
      />

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent overlay={false}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Saved')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('The changes have been saved.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleDialogClose}>
              {t('Ok')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={success.isSuccess}
        onOpenChange={() => {
          setSuccess({
            isSuccess: false,
            message: ''
          })
        }}
      >
        <AlertDialogContent overlay={false}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Notification')}</AlertDialogTitle>
            <AlertDialogDescription>{success.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setSuccess({
                  isSuccess: false,
                  message: ''
                })
              }}
            >
              {t('Ok')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={errors.isError}
        onOpenChange={() => {
          setErrors({
            isError: false,
            message: ''
          })
        }}
      >
        <AlertDialogContent overlay={false}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Notification')}</AlertDialogTitle>
            <AlertDialogDescription>{errors.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setErrors({
                  isError: false,
                  message: ''
                })
              }}
            >
              {t('Ok')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
